import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Plus, Pencil, Trash2, CreditCard } from 'lucide-react';
import Layout from '../components/Layout';
import { vault } from '../services/api';
import { useToast } from '../context/ToastContext';
import SensitiveField from '../components/vault/SensitiveField';
import VaultUnlockModal from '../components/vault/VaultUnlockModal';

interface Card {
  id: number; nickname: string | null; cardholder_name: string | null;
  card_number_masked: string | null; has_card_number: boolean;
  has_expiry: boolean; has_cvv: boolean; has_pin: boolean; card_type: string | null;
}
interface NetBanking {
  id: number; login_url: string | null;
  has_user_id: boolean; has_password: boolean; has_customer_id: boolean;
  has_profile_id: boolean; has_other_info: boolean;
}
interface OtherInfo {
  id: number; upi_id: string | null; registered_mobile_masked: string | null;
  has_registered_mobile: boolean; registered_email: string | null;
  branch_contact: string | null; relationship_manager: string | null;
  locker_info: string | null; nominee_info: string | null;
}
interface CustomField {
  id: number; field_name: string; is_sensitive: boolean; value: string | null; has_value: boolean;
}
interface AccountDetail {
  id: number; profile_id: number; bank_name: string | null; account_holder_name: string | null;
  account_number_masked: string | null; has_account_number: boolean; ifsc_code: string | null;
  branch: string | null; account_type: string | null; account_type_custom: string | null;
  has_customer_id: boolean; nickname: string | null; has_notes: boolean;
  cards: Card[]; net_banking: NetBanking | null; other_info: OtherInfo | null; custom_fields: CustomField[];
}

const ACCOUNT_TYPES = ['Savings', 'Current', 'Salary', 'NRE', 'NRO', 'Other'];

const VaultAccount: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const accountId = Number(id);
  const navigate = useNavigate();
  const { toast, confirm } = useToast();
  const [account, setAccount] = useState<AccountDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [unlockModalOpen, setUnlockModalOpen] = useState(false);
  const [showAccountEdit, setShowAccountEdit] = useState(false);
  const [showCardForm, setShowCardForm] = useState<Card | null | 'new'>(null);
  const [showCustomFieldForm, setShowCustomFieldForm] = useState(false);
  const [showNetBankingEdit, setShowNetBankingEdit] = useState(false);
  const [showOtherInfoEdit, setShowOtherInfoEdit] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    vault.getAccount(accountId).then(r => setAccount(r.data)).catch(() => toast('Unable to load bank account', 'error')).finally(() => setLoading(false));
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  const onLocked = () => setUnlockModalOpen(true);

  const handleDeleteAccount = async () => {
    const ok = await confirm('Delete this bank account? This permanently deletes its cards, net banking, and other vault data.');
    if (!ok) return;
    try {
      await vault.deleteAccount(accountId);
      navigate(account ? `/vault/profiles/${account.profile_id}` : '/vault');
    } catch { toast('Unable to delete bank account', 'error'); }
  };

  const handleDeleteCard = async (card: Card) => {
    const ok = await confirm(`Delete card "${card.nickname || card.card_type || 'card'}"?`);
    if (!ok) return;
    try { await vault.deleteCard(card.id); load(); } catch { toast('Unable to delete card', 'error'); }
  };

  const handleDeleteCustomField = async (field: CustomField) => {
    const ok = await confirm(`Delete field "${field.field_name}"?`);
    if (!ok) return;
    try { await vault.deleteCustomField(field.id); load(); } catch { toast('Unable to delete field', 'error'); }
  };

  if (loading || !account) {
    return <Layout title="Bank Account" subtitle=""><div style={{ color: 'var(--text-secondary)' }}>Loading...</div></Layout>;
  }

  return (
    <Layout title={account.bank_name || account.nickname || 'Bank Account'} subtitle={account.account_type || ''}>
      <button onClick={() => navigate(`/vault/profiles/${account.profile_id}`)} style={backLinkStyle}><ChevronLeft size={14} /> Back to Profile</button>

      <div style={sectionStyle}>
        <div style={sectionHeaderStyle}>
          <span>Account Information</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setShowAccountEdit(true)} style={iconButtonStyle}><Pencil size={14} /></button>
            <button onClick={handleDeleteAccount} style={iconButtonStyle}><Trash2 size={14} /></button>
          </div>
        </div>
        <InfoRow label="Bank Name" value={account.bank_name} />
        <InfoRow label="Account Holder" value={account.account_holder_name} />
        <SensitiveField label="Account Number" maskedPreview={account.account_number_masked || '••••••••'} hasValue={account.has_account_number}
          fetchValue={async () => (await vault.revealAccount(accountId)).data.account_number} onLockedError={onLocked} />
        <InfoRow label="IFSC Code" value={account.ifsc_code} />
        <InfoRow label="Branch" value={account.branch} />
        <InfoRow label="Account Type" value={account.account_type === 'Other' ? account.account_type_custom : account.account_type} />
        <SensitiveField label="Customer ID" maskedPreview="••••••••" hasValue={account.has_customer_id}
          fetchValue={async () => (await vault.revealAccount(accountId)).data.customer_id} onLockedError={onLocked} />
        <SensitiveField label="Notes" maskedPreview="••••••••" hasValue={account.has_notes}
          fetchValue={async () => (await vault.revealAccount(accountId)).data.notes} onLockedError={onLocked} />
      </div>

      <div style={sectionStyle}>
        <div style={sectionHeaderStyle}>
          <span>Debit Cards</span>
          <button onClick={() => setShowCardForm('new')} style={iconButtonStyle}><Plus size={14} /></button>
        </div>
        {account.cards.length === 0 ? (
          <div style={emptyStyle}>No cards added.</div>
        ) : account.cards.map(card => (
          <div key={card.id} style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 13 }}><CreditCard size={14} /> {card.nickname || card.card_type || 'Card'}</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setShowCardForm(card)} style={iconButtonStyle}><Pencil size={14} /></button>
                <button onClick={() => handleDeleteCard(card)} style={iconButtonStyle}><Trash2 size={14} /></button>
              </div>
            </div>
            <InfoRow label="Cardholder" value={card.cardholder_name} />
            <SensitiveField label="Card Number" maskedPreview={card.card_number_masked || '••••'} hasValue={card.has_card_number}
              fetchValue={async () => (await vault.revealCard(card.id)).data.card_number} onLockedError={onLocked} />
            <SensitiveField label="Expiry" maskedPreview="••/••" hasValue={card.has_expiry}
              fetchValue={async () => (await vault.revealCard(card.id)).data.expiry} onLockedError={onLocked} />
            <SensitiveField label="CVV" maskedPreview="•••" hasValue={card.has_cvv}
              fetchValue={async () => (await vault.revealCard(card.id)).data.cvv} onLockedError={onLocked} />
            <SensitiveField label="PIN" maskedPreview="••••" hasValue={card.has_pin}
              fetchValue={async () => (await vault.revealCard(card.id)).data.pin} onLockedError={onLocked} />
          </div>
        ))}
      </div>

      <div style={sectionStyle}>
        <div style={sectionHeaderStyle}>
          <span>Net Banking</span>
          <button onClick={() => setShowNetBankingEdit(true)} style={iconButtonStyle}><Pencil size={14} /></button>
        </div>
        {!account.net_banking ? (
          <div style={emptyStyle}>Not set up yet.</div>
        ) : (
          <>
            <InfoRow label="Login URL" value={account.net_banking.login_url} />
            <SensitiveField label="User ID" maskedPreview="••••••••" hasValue={account.net_banking.has_user_id}
              fetchValue={async () => (await vault.revealNetBanking(accountId)).data.user_id} onLockedError={onLocked} />
            <SensitiveField label="Password" maskedPreview="••••••••" hasValue={account.net_banking.has_password}
              fetchValue={async () => (await vault.revealNetBanking(accountId)).data.password} onLockedError={onLocked} />
            <SensitiveField label="Customer ID" maskedPreview="••••••••" hasValue={account.net_banking.has_customer_id}
              fetchValue={async () => (await vault.revealNetBanking(accountId)).data.customer_id} onLockedError={onLocked} />
            <SensitiveField label="Profile ID" maskedPreview="••••••••" hasValue={account.net_banking.has_profile_id}
              fetchValue={async () => (await vault.revealNetBanking(accountId)).data.profile_id} onLockedError={onLocked} />
            <SensitiveField label="Other Login Info" maskedPreview="••••••••" hasValue={account.net_banking.has_other_info}
              fetchValue={async () => (await vault.revealNetBanking(accountId)).data.other_info} onLockedError={onLocked} />
          </>
        )}
      </div>

      <div style={sectionStyle}>
        <div style={sectionHeaderStyle}>
          <span>Other Information</span>
          <button onClick={() => setShowOtherInfoEdit(true)} style={iconButtonStyle}><Pencil size={14} /></button>
        </div>
        {!account.other_info ? (
          <div style={emptyStyle}>Not set up yet.</div>
        ) : (
          <>
            <InfoRow label="UPI ID" value={account.other_info.upi_id} />
            <SensitiveField label="Registered Mobile" maskedPreview={account.other_info.registered_mobile_masked || '••••••••'} hasValue={account.other_info.has_registered_mobile}
              fetchValue={async () => (await vault.revealMobile(accountId)).data.registered_mobile} onLockedError={onLocked} />
            <InfoRow label="Registered Email" value={account.other_info.registered_email} />
            <InfoRow label="Branch Contact" value={account.other_info.branch_contact} />
            <InfoRow label="Relationship Manager" value={account.other_info.relationship_manager} />
            <InfoRow label="Locker Info" value={account.other_info.locker_info} />
            <InfoRow label="Nominee Info" value={account.other_info.nominee_info} />
          </>
        )}
      </div>

      <div style={sectionStyle}>
        <div style={sectionHeaderStyle}>
          <span>Custom Fields</span>
          <button onClick={() => setShowCustomFieldForm(true)} style={iconButtonStyle}><Plus size={14} /></button>
        </div>
        {account.custom_fields.length === 0 ? (
          <div style={emptyStyle}>No custom fields yet.</div>
        ) : account.custom_fields.map(field => (
          <div key={field.id} style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              {field.is_sensitive ? (
                <SensitiveField label={field.field_name} maskedPreview="••••••••" hasValue={field.has_value}
                  fetchValue={async () => (await vault.revealCustomField(field.id)).data.value} onLockedError={onLocked} />
              ) : (
                <InfoRow label={field.field_name} value={field.value} />
              )}
            </div>
            <button onClick={() => handleDeleteCustomField(field)} style={{ ...iconButtonStyle, marginLeft: 8 }}><Trash2 size={14} /></button>
          </div>
        ))}
      </div>

      {unlockModalOpen && <VaultUnlockModal onUnlock={() => setUnlockModalOpen(false)} onClose={() => setUnlockModalOpen(false)} />}
      {showAccountEdit && <AccountEditModal account={account} onClose={() => setShowAccountEdit(false)} onSaved={() => { setShowAccountEdit(false); load(); }} />}
      {showCardForm && <CardFormModal accountId={accountId} card={showCardForm === 'new' ? null : showCardForm} onClose={() => setShowCardForm(null)} onSaved={() => { setShowCardForm(null); load(); }} />}
      {showNetBankingEdit && <NetBankingEditModal accountId={accountId} onClose={() => setShowNetBankingEdit(false)} onSaved={() => { setShowNetBankingEdit(false); load(); }} />}
      {showOtherInfoEdit && <OtherInfoEditModal accountId={accountId} current={account.other_info} onClose={() => setShowOtherInfoEdit(false)} onSaved={() => { setShowOtherInfoEdit(false); load(); }} />}
      {showCustomFieldForm && <CustomFieldFormModal accountId={accountId} onClose={() => setShowCustomFieldForm(false)} onSaved={() => { setShowCustomFieldForm(false); load(); }} />}
    </Layout>
  );
};

const InfoRow: React.FC<{ label: string; value: string | null }> = ({ label, value }) => (
  <div style={rowStyle}>
    <span style={labelStyle}>{label}</span>
    <span style={valueStyle}>{value || <span style={{ color: 'var(--text-muted)' }}>Not set</span>}</span>
  </div>
);

const AccountEditModal: React.FC<{ account: AccountDetail; onClose: () => void; onSaved: () => void }> = ({ account, onClose, onSaved }) => {
  const { toast } = useToast();
  const [bankName, setBankName] = useState(account.bank_name || '');
  const [holderName, setHolderName] = useState(account.account_holder_name || '');
  const [accountNumber, setAccountNumber] = useState('');
  const [ifsc, setIfsc] = useState(account.ifsc_code || '');
  const [branch, setBranch] = useState(account.branch || '');
  const [accountType, setAccountType] = useState(account.account_type || '');
  const [accountTypeCustom, setAccountTypeCustom] = useState(account.account_type_custom || '');
  const [customerId, setCustomerId] = useState('');
  const [nickname, setNickname] = useState(account.nickname || '');
  const [notes, setNotes] = useState('');

  const ifscValid = !ifsc || /^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc);

  const handleSave = async () => {
    try {
      await vault.updateAccount(account.id, {
        bank_name: bankName, account_holder_name: holderName,
        ...(accountNumber ? { account_number: accountNumber } : {}),
        ifsc_code: ifsc, branch, account_type: accountType, account_type_custom: accountTypeCustom,
        ...(customerId ? { customer_id: customerId } : {}),
        nickname, ...(notes ? { notes } : {}),
      });
      onSaved();
    } catch (e: any) { toast(e?.response?.data?.detail || 'Unable to save', 'error'); }
  };

  return (
    <ModalShell title="Edit Account Information" onClose={onClose} onSave={handleSave}>
      <input value={bankName} onChange={e => setBankName(e.target.value)} placeholder="Bank name" style={inputStyle} />
      <input value={holderName} onChange={e => setHolderName(e.target.value)} placeholder="Account holder name" style={{ ...inputStyle, marginTop: 10 }} />
      <input value={accountNumber} onChange={e => setAccountNumber(e.target.value)} placeholder="Account number (leave blank to keep unchanged)" style={{ ...inputStyle, marginTop: 10 }} />
      <input value={ifsc} onChange={e => setIfsc(e.target.value.toUpperCase())} placeholder="IFSC code" style={{ ...inputStyle, marginTop: 10, borderColor: ifscValid ? 'var(--border)' : 'var(--accent-amber)' }} />
      {!ifscValid && <div style={{ fontSize: 11, color: 'var(--accent-amber)', marginTop: 4 }}>Doesn't look like a standard IFSC code — you can still save it.</div>}
      <input value={branch} onChange={e => setBranch(e.target.value)} placeholder="Branch" style={{ ...inputStyle, marginTop: 10 }} />
      <select value={accountType} onChange={e => setAccountType(e.target.value)} style={{ ...inputStyle, marginTop: 10 }}>
        <option value="">Account type...</option>
        {ACCOUNT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
      </select>
      {accountType === 'Other' && <input value={accountTypeCustom} onChange={e => setAccountTypeCustom(e.target.value)} placeholder="Custom account type" style={{ ...inputStyle, marginTop: 10 }} />}
      <input value={customerId} onChange={e => setCustomerId(e.target.value)} placeholder="Customer ID (leave blank to keep unchanged)" style={{ ...inputStyle, marginTop: 10 }} />
      <input value={nickname} onChange={e => setNickname(e.target.value)} placeholder="Account nickname" style={{ ...inputStyle, marginTop: 10 }} />
      <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (leave blank to keep unchanged)" style={{ ...inputStyle, marginTop: 10, minHeight: 70 }} />
    </ModalShell>
  );
};

const CardFormModal: React.FC<{ accountId: number; card: Card | null; onClose: () => void; onSaved: () => void }> = ({ accountId, card, onClose, onSaved }) => {
  const { toast } = useToast();
  const [nickname, setNickname] = useState(card?.nickname || '');
  const [cardholderName, setCardholderName] = useState(card?.cardholder_name || '');
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [pin, setPin] = useState('');
  const [cardType, setCardType] = useState(card?.card_type || '');

  const luhnValid = (num: string) => {
    const digits = num.replace(/\s/g, '');
    if (!/^\d{13,19}$/.test(digits)) return false;
    let sum = 0, alt = false;
    for (let i = digits.length - 1; i >= 0; i--) {
      let n = parseInt(digits[i], 10);
      if (alt) { n *= 2; if (n > 9) n -= 9; }
      sum += n; alt = !alt;
    }
    return sum % 10 === 0;
  };
  const cardNumberValid = !cardNumber || luhnValid(cardNumber);
  const expiryValid = !expiry || /^(0[1-9]|1[0-2])\/\d{2}$/.test(expiry);

  const handleSave = async () => {
    const payload = {
      nickname, cardholder_name: cardholderName,
      ...(cardNumber ? { card_number: cardNumber } : {}),
      ...(expiry ? { expiry } : {}),
      ...(cvv ? { cvv } : {}),
      ...(pin ? { pin } : {}),
      card_type: cardType,
    };
    try {
      if (card) await vault.updateCard(card.id, payload);
      else await vault.createCard(accountId, payload);
      onSaved();
    } catch (e: any) { toast(e?.response?.data?.detail || 'Unable to save card', 'error'); }
  };

  return (
    <ModalShell title={card ? 'Edit Debit Card' : 'Add Debit Card'} onClose={onClose} onSave={handleSave}>
      <input value={nickname} onChange={e => setNickname(e.target.value)} placeholder="Card nickname" style={inputStyle} />
      <input value={cardholderName} onChange={e => setCardholderName(e.target.value)} placeholder="Cardholder name" style={{ ...inputStyle, marginTop: 10 }} />
      <input value={cardNumber} onChange={e => setCardNumber(e.target.value)} placeholder={card ? 'Card number (leave blank to keep unchanged)' : 'Card number'}
        style={{ ...inputStyle, marginTop: 10, borderColor: cardNumberValid ? 'var(--border)' : 'var(--accent-amber)' }} />
      {!cardNumberValid && <div style={{ fontSize: 11, color: 'var(--accent-amber)', marginTop: 4 }}>Doesn't look like a valid card number — you can still save it.</div>}
      <input value={expiry} onChange={e => setExpiry(e.target.value)} placeholder="Expiry (MM/YY)"
        style={{ ...inputStyle, marginTop: 10, borderColor: expiryValid ? 'var(--border)' : 'var(--accent-amber)' }} />
      {!expiryValid && <div style={{ fontSize: 11, color: 'var(--accent-amber)', marginTop: 4 }}>Expected MM/YY — you can still save it.</div>}
      <input value={cvv} onChange={e => setCvv(e.target.value)} placeholder="CVV" style={{ ...inputStyle, marginTop: 10 }} />
      <input value={pin} onChange={e => setPin(e.target.value)} placeholder="Card PIN" style={{ ...inputStyle, marginTop: 10 }} />
      <input value={cardType} onChange={e => setCardType(e.target.value)} placeholder="Card type (Visa, Mastercard, RuPay...)" style={{ ...inputStyle, marginTop: 10 }} />
    </ModalShell>
  );
};

const NetBankingEditModal: React.FC<{ accountId: number; onClose: () => void; onSaved: () => void }> = ({ accountId, onClose, onSaved }) => {
  const { toast } = useToast();
  const [loginUrl, setLoginUrl] = useState('');
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [profileId, setProfileId] = useState('');
  const [otherInfo, setOtherInfo] = useState('');

  const handleSave = async () => {
    try {
      await vault.updateNetBanking(accountId, {
        ...(loginUrl ? { login_url: loginUrl } : {}),
        ...(userId ? { user_id: userId } : {}),
        ...(password ? { password } : {}),
        ...(customerId ? { customer_id: customerId } : {}),
        ...(profileId ? { profile_id: profileId } : {}),
        ...(otherInfo ? { other_info: otherInfo } : {}),
      });
      onSaved();
    } catch { toast('Unable to save net banking info', 'error'); }
  };

  return (
    <ModalShell title="Edit Net Banking" onClose={onClose} onSave={handleSave}>
      <input value={loginUrl} onChange={e => setLoginUrl(e.target.value)} placeholder="Login URL" style={inputStyle} />
      <input value={userId} onChange={e => setUserId(e.target.value)} placeholder="User ID (leave blank to keep unchanged)" style={{ ...inputStyle, marginTop: 10 }} />
      <input value={password} onChange={e => setPassword(e.target.value)} placeholder="Password (leave blank to keep unchanged)" style={{ ...inputStyle, marginTop: 10 }} />
      <input value={customerId} onChange={e => setCustomerId(e.target.value)} placeholder="Customer ID (leave blank to keep unchanged)" style={{ ...inputStyle, marginTop: 10 }} />
      <input value={profileId} onChange={e => setProfileId(e.target.value)} placeholder="Profile ID (leave blank to keep unchanged)" style={{ ...inputStyle, marginTop: 10 }} />
      <textarea value={otherInfo} onChange={e => setOtherInfo(e.target.value)} placeholder="Other login info (leave blank to keep unchanged)" style={{ ...inputStyle, marginTop: 10, minHeight: 60 }} />
    </ModalShell>
  );
};

const OtherInfoEditModal: React.FC<{ accountId: number; current: OtherInfo | null; onClose: () => void; onSaved: () => void }> = ({ accountId, current, onClose, onSaved }) => {
  const { toast } = useToast();
  const [upiId, setUpiId] = useState(current?.upi_id || '');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState(current?.registered_email || '');
  const [branchContact, setBranchContact] = useState(current?.branch_contact || '');
  const [rm, setRm] = useState(current?.relationship_manager || '');
  const [locker, setLocker] = useState(current?.locker_info || '');
  const [nominee, setNominee] = useState(current?.nominee_info || '');

  const handleSave = async () => {
    try {
      await vault.updateOtherInfo(accountId, {
        upi_id: upiId, ...(mobile ? { registered_mobile: mobile } : {}),
        registered_email: email, branch_contact: branchContact,
        relationship_manager: rm, locker_info: locker, nominee_info: nominee,
      });
      onSaved();
    } catch { toast('Unable to save', 'error'); }
  };

  return (
    <ModalShell title="Edit Other Information" onClose={onClose} onSave={handleSave}>
      <input value={upiId} onChange={e => setUpiId(e.target.value)} placeholder="UPI ID" style={inputStyle} />
      <input value={mobile} onChange={e => setMobile(e.target.value)} placeholder="Registered mobile (leave blank to keep unchanged)" style={{ ...inputStyle, marginTop: 10 }} />
      <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Registered email" style={{ ...inputStyle, marginTop: 10 }} />
      <input value={branchContact} onChange={e => setBranchContact(e.target.value)} placeholder="Branch contact" style={{ ...inputStyle, marginTop: 10 }} />
      <input value={rm} onChange={e => setRm(e.target.value)} placeholder="Relationship manager" style={{ ...inputStyle, marginTop: 10 }} />
      <input value={locker} onChange={e => setLocker(e.target.value)} placeholder="Locker information" style={{ ...inputStyle, marginTop: 10 }} />
      <input value={nominee} onChange={e => setNominee(e.target.value)} placeholder="Nominee information" style={{ ...inputStyle, marginTop: 10 }} />
    </ModalShell>
  );
};

const CustomFieldFormModal: React.FC<{ accountId: number; onClose: () => void; onSaved: () => void }> = ({ accountId, onClose, onSaved }) => {
  const { toast } = useToast();
  const [fieldName, setFieldName] = useState('');
  const [fieldValue, setFieldValue] = useState('');
  const [isSensitive, setIsSensitive] = useState(false);

  const handleSave = async () => {
    if (!fieldName.trim()) { toast('Field name is required', 'error'); return; }
    try {
      await vault.createCustomField(accountId, { field_name: fieldName, field_value: fieldValue, is_sensitive: isSensitive });
      onSaved();
    } catch (e: any) { toast(e?.response?.data?.detail || 'Unable to add field', 'error'); }
  };

  return (
    <ModalShell title="Add Custom Field" onClose={onClose} onSave={handleSave}>
      <input value={fieldName} onChange={e => setFieldName(e.target.value)} placeholder="Field name (e.g. Locker Number)" style={inputStyle} />
      <input value={fieldValue} onChange={e => setFieldValue(e.target.value)} placeholder="Field value (e.g. A-142)" style={{ ...inputStyle, marginTop: 10 }} />
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 13, color: 'var(--text-secondary)' }}>
        <input type="checkbox" checked={isSensitive} onChange={e => setIsSensitive(e.target.checked)} />
        Treat this value as sensitive (masked, requires vault unlock to reveal)
      </label>
    </ModalShell>
  );
};

const ModalShell: React.FC<{ title: string; onClose: () => void; onSave: () => void; children: React.ReactNode }> = ({ title, onClose, onSave, children }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9998, overflowY: 'auto', padding: 20 }}>
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: 28, maxWidth: 420, width: '100%' }}>
      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>{title}</div>
      {children}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
        <button onClick={onClose} style={cancelButtonStyle}>Cancel</button>
        <button onClick={onSave} style={primaryButtonStyle}>Save</button>
      </div>
    </div>
  </div>
);

const sectionStyle: React.CSSProperties = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, marginBottom: 16 };
const sectionHeaderStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 14, fontWeight: 700, marginBottom: 10, color: 'var(--text-primary)' };
const rowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border)' };
const labelStyle: React.CSSProperties = { fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 };
const valueStyle: React.CSSProperties = { fontSize: 14, color: 'var(--text-primary)' };
const emptyStyle: React.CSSProperties = { fontSize: 13, color: 'var(--text-muted)', padding: '8px 0' };
const inputStyle: React.CSSProperties = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 14 };
const primaryButtonStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', background: 'var(--accent-blue)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const cancelButtonStyle: React.CSSProperties = { padding: '9px 20px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-secondary)', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const iconButtonStyle: React.CSSProperties = { background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 6, padding: 6, cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex' };
const backLinkStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 4 };

export default VaultAccount;
